import Header from "../components/Header";
import Footer from "../components/Footer";
import SEO from "../components/SEO";
import logoCircle from "figma:asset/aa1f9ee0ccf249c5893f7da5504b2e60ae897567.png";

export default function YesProgram() {
  const curriculum = [
    {
      number: 1,
      title: "Foundations of Computation",
      weeks: "Weeks 1–2",
      subtitle: "How computers reason about problems",
      topics: ["Algorithms & logic", "Inputs/outputs", "Abstraction", "Basic Python"],
      color: "#184b9d",
    },
    {
      number: 2,
      title: "Data & Reality",
      weeks: "Weeks 3–4",
      subtitle: "Understanding data as imperfect representations",
      topics: ["Data collection", "Cleaning & bias", "Visualization", "Real datasets"],
      color: "#1e8eaf",
    },
    {
      number: 3,
      title: "Full-Stack Systems",
      weeks: "Weeks 5–7",
      subtitle: "Build a working software system end-to-end",
      topics: ["Frontend & backend", "APIs & databases", "Data flow", "Web systems"],
      color: "#90CAF3",
    },
    {
      number: 4,
      title: "Intelligence & Responsibility",
      weeks: "Weeks 8–9",
      subtitle: "Understanding when and why to use AI",
      topics: ["Rule-based systems", "Intro to ML", "Ethics & trust", "Consequences"],
      color: "#184b9d",
    },
    {
      number: 5,
      title: "Reflection & Showcase",
      weeks: "Week 10",
      subtitle: "Articulate reasoning and learning",
      topics: ["Project demos", "Reflection sessions", "Systems thinking", "Communication"],
      color: "#ffcc10",
    },
  ];

  return (
    <div className="bg-white min-h-screen">
      <SEO
        title="YES Program - Young Engineers & Scientists | Unpatterned"
        description="A 10-week hands-on learning program for young engineers and scientists aged 15-20. Learn computational thinking, systems design, AI safety, and responsible technology development."
        keywords={[
          "YES Program",
          "young engineers",
          "scientists program",
          "technology education",
          "coding bootcamp",
          "youth STEM program",
          "AI education",
          "systems thinking",
          "Africa tech education",
          "computational thinking",
          "programming for teens",
          "technology mentorship",
          "ethical AI",
          "software engineering education",
        ]}
        url="https://unpatterned.org/yes-program"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "EducationalProgram",
          "name": "YES Program - Young Engineers & Scientists",
          "description": "A hands-on learning initiative for young people to understand how modern technology systems are built, understood, and used responsibly.",
          "provider": {
            "@type": "Organization",
            "name": "Unpatterned",
            "url": "https://unpatterned.org"
          },
          "educationalProgramMode": "Hybrid",
          "timeToComplete": "P10W",
          "typicalAgeRange": "15-20",
          "numberOfCredits": 0,
          "programPrerequisites": "Curiosity and commitment to learning",
          "occupationalCategory": "Software Engineer, Data Scientist, Systems Designer",
          "hasCourse": [
            {
              "@type": "Course",
              "name": "Foundations of Computation",
              "description": "How computers reason about problems"
            },
            {
              "@type": "Course",
              "name": "Data & Reality",
              "description": "Understanding data as imperfect representations"
            },
            {
              "@type": "Course",
              "name": "Full-Stack Systems",
              "description": "Build a working software system end-to-end"
            },
            {
              "@type": "Course",
              "name": "Intelligence & Responsibility",
              "description": "Understanding when and why to use AI"
            }
          ]
        }}
      />
      <Header />

      {/* Hero Section */}
      <section className="pt-32 md:pt-40 pb-16 md:pb-20 px-4 md:px-6 bg-gradient-to-b from-white to-[rgba(255,204,16,0.05)]">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center justify-center mb-6 md:mb-8">
            <img src={logoCircle} alt="YES Program" className="w-24 h-24 md:w-32 md:h-32" />
          </div>
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-[rgba(255,204,16,0.15)] text-[#050e1d] px-4 py-2 rounded-full font-['Manrope',sans-serif] text-sm font-medium mb-6">
              <span className="w-2 h-2 bg-[#ffcc10] rounded-full animate-pulse"></span>
              Summer 2026 Pilot Program
            </div>
            <h1 className="font-['DM_Serif_Display',serif] text-4xl sm:text-5xl md:text-6xl text-[#050e1d] mb-4 md:mb-6 px-4">
              Young Engineers & Scientists Program
            </h1>
            <p className="font-['Manrope',sans-serif] text-lg sm:text-xl md:text-2xl text-[#050e1d]/70 max-w-4xl mx-auto leading-relaxed px-4">
              A hands-on learning initiative for young people to understand how modern technology systems are built, understood, and used responsibly.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12 md:mb-16 px-4">
            <button className="bg-[#ffcc10] text-[#050e1d] px-8 md:px-10 py-3 md:py-4 rounded-lg font-['Manrope',sans-serif] font-semibold text-base md:text-lg hover:bg-[#ffcc10]/90 transition-all hover:scale-105">
              Apply as Participant
            </button>
            <button className="bg-[#184b9d] text-white px-8 md:px-10 py-3 md:py-4 rounded-lg font-['Manrope',sans-serif] font-semibold text-base md:text-lg hover:bg-[#1e8eaf] transition-all">
              Become a Mentor
            </button>
          </div>

          {/* Program Details Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            <div className="bg-white p-4 md:p-6 rounded-xl border border-[rgba(5,14,26,0.1)] text-center">
              <div className="font-['Manrope',sans-serif] text-xs md:text-sm text-[#050e1d]/50 mb-2">Duration</div>
              <div className="font-['Manrope',sans-serif] font-semibold text-lg md:text-xl text-[#050e1d] mb-1">8–10 weeks</div>
              <div className="font-['Manrope',sans-serif] text-xs md:text-sm text-[#050e1d]/70">August/Sept – October</div>
            </div>
            <div className="bg-white p-4 md:p-6 rounded-xl border border-[rgba(5,14,26,0.1)] text-center">
              <div className="font-['Manrope',sans-serif] text-xs md:text-sm text-[#050e1d]/50 mb-2">Schedule</div>
              <div className="font-['Manrope',sans-serif] font-semibold text-lg md:text-xl text-[#050e1d] mb-1">Weekend sessions</div>
              <div className="font-['Manrope',sans-serif] text-xs md:text-sm text-[#050e1d]/70">2–3 hours per week</div>
            </div>
            <div className="bg-white p-4 md:p-6 rounded-xl border border-[rgba(5,14,26,0.1)] text-center">
              <div className="font-['Manrope',sans-serif] text-xs md:text-sm text-[#050e1d]/50 mb-2">Cohort Size</div>
              <div className="font-['Manrope',sans-serif] font-semibold text-lg md:text-xl text-[#050e1d] mb-1">15–20 participants</div>
              <div className="font-['Manrope',sans-serif] text-xs md:text-sm text-[#050e1d]/70">Pilot cohort</div>
            </div>
            <div className="bg-white p-4 md:p-6 rounded-xl border border-[rgba(5,14,26,0.1)] text-center">
              <div className="font-['Manrope',sans-serif] text-xs md:text-sm text-[#050e1d]/50 mb-2">Format</div>
              <div className="font-['Manrope',sans-serif] font-semibold text-lg md:text-xl text-[#050e1d] mb-1">Hybrid</div>
              <div className="font-['Manrope',sans-serif] text-xs md:text-sm text-[#050e1d]/70">Online & in-person</div>
            </div>
          </div>
        </div>
      </section>

      {/* What Makes This Different */}
      <section className="py-24 px-6">
        <div className="max-w-5xl mx-auto">
          <h2 className="font-['DM_Serif_Display',serif] text-5xl text-[#050e1d] mb-8 text-center">
            What Makes This Different
          </h2>
          <p className="font-['Manrope',sans-serif] text-xl text-[#050e1d]/70 mb-12 leading-relaxed max-w-4xl mx-auto">
            Rather than focusing on tools alone, the program emphasizes reasoning, systems thinking, and responsible technology use. Participants learn how software, data, and intelligence come together end-to-end—while critically examining how these systems interact with society, institutions, and everyday life.
          </p>
          <p className="font-['Manrope',sans-serif] text-lg text-[#050e1d]/60 mb-12 max-w-4xl mx-auto">
            The program is especially designed for contexts where resources, data, and infrastructure are constrained, helping participants develop adaptable thinking skills that apply across Africa, the Global South, and beyond.
          </p>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-gradient-to-br from-[rgba(24,75,157,0.05)] to-[rgba(30,142,175,0.05)] p-8 rounded-xl border border-[rgba(24,75,157,0.1)]">
              <div className="w-12 h-12 bg-[#184b9d] rounded-lg flex items-center justify-center mb-6">
                <span className="text-white text-2xl">✓</span>
              </div>
              <h3 className="font-['Manrope',sans-serif] font-semibold text-xl text-[#050e1d] mb-3">
                Focuses on reasoning and systems, not just coding
              </h3>
            </div>
            <div className="bg-gradient-to-br from-[rgba(30,142,175,0.05)] to-[rgba(144,202,243,0.05)] p-8 rounded-xl border border-[rgba(30,142,175,0.1)]">
              <div className="w-12 h-12 bg-[#1e8eaf] rounded-lg flex items-center justify-center mb-6">
                <span className="text-white text-2xl">✓</span>
              </div>
              <h3 className="font-['Manrope',sans-serif] font-semibold text-xl text-[#050e1d] mb-3">
                Builds an end-to-end understanding of technology
              </h3>
            </div>
            <div className="bg-gradient-to-br from-[rgba(144,202,243,0.05)] to-[rgba(24,75,157,0.05)] p-8 rounded-xl border border-[rgba(144,202,243,0.1)]">
              <div className="w-12 h-12 bg-[#90CAF3] rounded-lg flex items-center justify-center mb-6">
                <span className="text-white text-2xl">✓</span>
              </div>
              <h3 className="font-['Manrope',sans-serif] font-semibold text-xl text-[#050e1d] mb-3">
                Grounded in real-world constraints and contexts
              </h3>
            </div>
            <div className="bg-gradient-to-br from-[rgba(255,204,16,0.05)] to-[rgba(24,75,157,0.05)] p-8 rounded-xl border border-[rgba(255,204,16,0.1)]">
              <div className="w-12 h-12 bg-[#ffcc10] rounded-lg flex items-center justify-center mb-6">
                <span className="text-white text-2xl">✓</span>
              </div>
              <h3 className="font-['Manrope',sans-serif] font-semibold text-xl text-[#050e1d] mb-3">
                Introduces AI responsibly, without hype
              </h3>
            </div>
          </div>
        </div>
      </section>

      {/* Curriculum */}
      <section className="py-24 px-6 bg-gradient-to-b from-white to-[rgba(24,75,157,0.03)]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-['DM_Serif_Display',serif] text-5xl text-[#050e1d] mb-6">
              Curriculum
            </h2>
            <p className="font-['Manrope',sans-serif] text-xl text-[#050e1d]/70">
              A 10-week journey from foundations to full systems
            </p>
          </div>

          <div className="space-y-6">
            {curriculum.map((module, index) => (
              <div 
                key={index}
                className="bg-white rounded-xl border border-[rgba(5,14,26,0.1)] p-8 hover:shadow-lg transition-all"
              >
                <div className="flex items-start gap-8">
                  <div 
                    className="w-16 h-16 rounded-xl flex items-center justify-center shrink-0"
                    style={{ backgroundColor: `${module.color}15`, borderColor: module.color, borderWidth: '2px' }}
                  >
                    <span className="font-['Manrope',sans-serif] font-bold text-2xl" style={{ color: module.color }}>
                      {module.number}
                    </span>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="font-['DM_Serif_Display',serif] text-2xl text-[#050e1d] mb-2">
                          {module.title}
                        </h3>
                        <div className="font-['Manrope',sans-serif] text-sm text-[#050e1d]/50 mb-2">
                          {module.weeks}
                        </div>
                      </div>
                    </div>
                    <p className="font-['Manrope',sans-serif] text-[#050e1d]/70 mb-4">
                      {module.subtitle}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {module.topics.map((topic, i) => (
                        <span 
                          key={i}
                          className="px-4 py-2 rounded-full text-sm font-['Manrope',sans-serif]"
                          style={{ 
                            backgroundColor: `${module.color}10`,
                            color: module.color 
                          }}
                        >
                          {topic}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What Participants Will Learn */}
      <section className="py-24 px-6">
        <div className="max-w-5xl mx-auto">
          <h2 className="font-['DM_Serif_Display',serif] text-5xl text-[#050e1d] mb-12 text-center">
            What Participants Will Learn
          </h2>
          <div className="grid md:grid-cols-2 gap-6 mb-12">
            {[
              "Understand how computers and algorithms reason about problems",
              "Learn how real-world data is collected, cleaned, and interpreted",
              "Build a simple but complete full-stack system from scratch",
              "Develop foundational skills in software engineering and data science",
              "Gain early exposure to artificial intelligence—without hype",
              "Practice reasoning about ethics, trust, and unintended consequences",
              "Build confidence to continue learning independently",
            ].map((item, index) => (
              <div key={index} className="flex items-start gap-4 p-6 bg-[rgba(24,75,157,0.03)] rounded-lg">
                <div className="w-6 h-6 bg-[#184b9d] rounded-full flex items-center justify-center shrink-0 mt-1">
                  <span className="text-white text-xs">✓</span>
                </div>
                <p className="font-['Manrope',sans-serif] text-lg text-[#050e1d]/80">
                  {item}
                </p>
              </div>
            ))}
          </div>
          <div className="bg-[rgba(24,75,157,0.05)] p-8 rounded-xl border-l-4 border-[#184b9d]">
            <p className="font-['Manrope',sans-serif] text-lg text-[#050e1d]/80 italic">
              The emphasis is not on becoming experts, but on building coherent mental models of how technology systems work.
            </p>
          </div>
        </div>
      </section>

      {/* Who Should Apply */}
      <section className="py-24 px-6 bg-gradient-to-b from-white to-[rgba(144,202,243,0.05)]">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-['DM_Serif_Display',serif] text-5xl text-[#050e1d] mb-8 text-center">
            Who Should Apply
          </h2>
          <p className="font-['Manrope',sans-serif] text-xl text-[#050e1d]/70 mb-12 text-center leading-relaxed">
            This program is designed for curious students and early-career learners (approximately ages 15–20) interested in engineering, science, or technology.
          </p>
          <div className="grid md:grid-cols-2 gap-6 mb-8">
            {[
              "Basic math literacy and curiosity",
              "Little or no prior programming experience",
              "Enthusiasm for learning how technology works",
            ].map((item, index) => (
              <div key={index} className="flex items-center gap-4 p-6 bg-white rounded-lg border border-[rgba(5,14,26,0.1)]">
                <span className="text-[#184b9d] text-2xl">•</span>
                <p className="font-['Manrope',sans-serif] text-lg text-[#050e1d]/80">
                  {item}
                </p>
              </div>
            ))}
          </div>
          <div className="text-center">
            <p className="font-['Manrope',sans-serif] text-lg text-[#050e1d]/60 font-medium">
              No advanced coding background is required.
            </p>
          </div>
        </div>
      </section>

      {/* Ethics & Safeguarding */}
      <section className="py-16 px-6 bg-[rgba(30,142,175,0.05)]">
        <div className="max-w-4xl mx-auto text-center">
          <h3 className="font-['Manrope',sans-serif] font-semibold text-2xl text-[#050e1d] mb-4">
            Ethics & Safeguarding
          </h3>
          <p className="font-['Manrope',sans-serif] text-lg text-[#050e1d]/70 leading-relaxed">
            The program is delivered in partnership with experienced youth organizations and follows strict safeguarding, supervision, and consent guidelines to ensure a safe and inclusive learning environment.
          </p>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-32 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-['DM_Serif_Display',serif] text-5xl text-[#050e1d] mb-8">
            Ready to Join?
          </h2>
          <p className="font-['Manrope',sans-serif] text-xl text-[#050e1d]/70 mb-12 leading-relaxed">
            Applications open soon. Join the pilot cohort and help us shape the future of technology education.
          </p>
          <div className="flex gap-4 justify-center">
            <button className="bg-[#ffcc10] text-[#050e1d] px-12 py-5 rounded-lg font-['Manrope',sans-serif] font-semibold text-xl hover:bg-[#ffcc10]/90 transition-all hover:scale-105">
              Apply as Participant
            </button>
            <button className="bg-[#184b9d] text-white px-12 py-5 rounded-lg font-['Manrope',sans-serif] font-semibold text-xl hover:bg-[#1e8eaf] transition-all hover:scale-105">
              Become a Mentor
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}